import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAtomValue } from "jotai";
import { RetroplanningApi } from "@/config/apiClient";
import { createApiClient } from "@/config/createApiClient";
import { currentWorkspaceAtom } from "@/store";

const apiClient = createApiClient(RetroplanningApi);

export const useRetroplanning = () => {
  const currentWorkspace = useAtomValue(currentWorkspaceAtom);

  const queryClient = useQueryClient();

  const useAddRetroplanning = (params: {
    orderId: string;
    orderItemId: string;
  }) =>
    useMutation({
      mutationKey: ["retroplanning", params],
      mutationFn: (
        body: Parameters<
          typeof apiClient.retroplanningsControllerCreate
        >["0"]["retroplanningItemCreateOrUpdateCommand"]
      ) =>
        apiClient.retroplanningsControllerCreate({
          accountId: currentWorkspace?.accountId,
          organizationId: currentWorkspace?.organizationId,
          workspaceId: currentWorkspace?.id,
          orderItemId: params.orderItemId,
          retroplanningItemCreateOrUpdateCommand: body,
        }),
      onSuccess: () =>
        Promise.all([
          queryClient.refetchQueries({ queryKey: ["order", params.orderId] }),
          queryClient.invalidateQueries({
            queryKey: ["orderItem", params.orderId, params.orderItemId],
          }),
          queryClient.refetchQueries({
            queryKey: ["orderItemLogs", params.orderId, params.orderItemId],
          }),
          queryClient.invalidateQueries({ queryKey: ["worklist"] }),
        ]),
    });

  const useUpdateRetroplanning = (params: {
    orderId: string;
    orderItemId: string;
  }) => {
    return useMutation({
      mutationKey: ["retroplanning"],
      mutationFn: (
        body: Parameters<
          typeof apiClient.retroplanningsControllerUpdate
        >["0"]["retroplanningItemCreateOrUpdateCommand"]
      ) =>
        apiClient.retroplanningsControllerUpdate({
          accountId: currentWorkspace?.accountId,
          organizationId: currentWorkspace?.organizationId,
          workspaceId: currentWorkspace?.id,
          orderItemId: params.orderItemId,
          retroplanningItemCreateOrUpdateCommand: body,
        }),
      onSuccess: () =>
        Promise.all([
          queryClient.refetchQueries({ queryKey: ["order", params.orderId] }),
          queryClient.refetchQueries({ queryKey: ["retroplanning", params] }),
          queryClient.invalidateQueries({
            queryKey: ["orderItem", params.orderId, params.orderItemId],
          }),
          queryClient.refetchQueries({
            queryKey: ["orderItemLogs", params.orderId, params.orderItemId],
          }),
          queryClient.invalidateQueries({ queryKey: ["worklist"] }),
        ]),
    });
  };

  const useShareRetroplanning = () => {
    return useMutation({
      mutationKey: ["retroplanning"],
      mutationFn: (params: { orderId: string; orderItemId: string }) =>
        apiClient.retroplanningsControllerUpdateIsSharedAt({
          accountId: currentWorkspace?.accountId,
          organizationId: currentWorkspace?.organizationId,
          workspaceId: currentWorkspace?.id,
          orderItemId: params.orderItemId,
        }),
      onSuccess: (_, params) =>
        Promise.all([
          queryClient.refetchQueries({ queryKey: ["order", params.orderId] }),
          queryClient.refetchQueries({ queryKey: ["retroplanning", params] }),
          queryClient.invalidateQueries({
            queryKey: ["orderItem", params.orderId, params.orderItemId],
          }),
          queryClient.refetchQueries({
            queryKey: ["orderItemLogs", params.orderId, params.orderItemId],
          }),
          queryClient.invalidateQueries({ queryKey: ["worklist"] }),
        ]),
    });
  };

  return {
    useAddRetroplanning,
    useUpdateRetroplanning,
    useShareRetroplanning,
  };
};
